import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/constants";
import { LayoutDashboard, Truck, MessageSquare, Mail, ClipboardList, Users, Gavel, ShieldCheck, LogOut, FileText, User, Building2, HardHat, ClipboardCheck, LayoutGrid, LineChart, Crown, Package, UserPlus, CheckSquare, KeyRound, UsersRound, Bot, Handshake, Siren, BarChart3, UserCog, Activity, BellRing } from "lucide-react";
import ChangePasswordModal from "@/components/dashboard/ChangePasswordModal";
import DevicesModal from "@/components/dashboard/DevicesModal";
import ProfileModal from "@/components/ProfileModal";
import TrialRibbon from "@/components/TrialRibbon";
import NotificationBell from "@/components/NotificationBell";
import api from "@/lib/api";

const navByRole = {
  vendor: [
    { to: "/vendor", icon: LayoutDashboard, label: "Overview", end: true },
    { to: "/vendor/listings", icon: Truck, label: "My Fleet" },
    { to: "/vendor/leads", icon: MessageSquare, label: "Leads & Quotes" },
    { to: "/vendor/profile", icon: User, label: "Profile & KYC" },
  ],
  tenant_owner: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Overview", end: true },
    { to: "/dashboard/smart-fleet", icon: Truck, label: "Fleet + Maint" },
    { to: "/dashboard/reports", icon: FileText, label: "Reports" },
    { to: "/dashboard/profile", icon: User, label: "Profile" },
  ],
  customer: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Overview", end: true },
    { to: "/dashboard/requirements", icon: ClipboardList, label: "My Requirements" },
    { to: "/dashboard/quotes", icon: FileText, label: "Received Quotes" },
    { to: "/dashboard/reports", icon: FileText, label: "Reports" },
    { to: "/dashboard/notifications", icon: BellRing, label: "Notifications" },
    { to: "/dashboard/profile", icon: User, label: "Profile" },
  ],
  // Admin nav — grouped by Master Platform Taxonomy (Feb 2026 structural review)
  admin: [
    { section: "Console", items: [
      { to: "/admin",             icon: LayoutDashboard, label: "Overview",       end: true },
      { to: "/admin/launcher",    icon: LayoutGrid,      label: "App Launcher",   adminOnly: true },
      { to: "/admin/live-ops",    icon: Activity,        label: "Live Ops Watch", adminOnly: true },
      { to: "/admin/incidents",   icon: BellRing,        label: "Incident Log",   adminOnly: true },
      { to: "/admin/founder",     icon: Crown,           label: "Founder",        adminOnly: true },
      { to: "/admin/health-check",icon: Activity,        label: "Health Check",   adminOnly: true },
    ]},
    { section: "Marketplace", items: [
      { to: "/admin/marketplace-listings", icon: LayoutGrid, label: "Listings Moderation", adminOnly: true },
      { to: "/admin/listings",         icon: Truck,      label: "Rental",           perm: "registrations" },
      { to: "/admin/buy-new",          icon: Truck,      label: "New Equipment",    perm: "registrations" },
      { to: "/admin/workforce",        icon: HardHat,    label: "Workforce",        perm: "registrations" },
      { to: "/admin/inspection",       icon: ClipboardCheck, label: "Inspection",   perm: "registrations" },
      { to: "/admin/spares",           icon: Package,    label: "Industrial Materials",perm: "registrations" },
      { to: "/admin/scrap/buyers",     icon: Package,    label: "Scrap Buyers",     adminOnly: true },
      { to: "/admin/careers",          icon: UserPlus,   label: "Careers · Jobs",   adminOnly: true },
      { to: "/admin/auctions",         icon: Gavel,      label: "Auctions",         perm: "supplier_directory" },
      { to: "/admin/vendor-directory", icon: Building2,  label: "Suppliers Directory", perm: "supplier_directory" },
      { to: "/admin/users",            icon: Users,      label: "Users & KYC",      perm: "users" },
      { to: "/admin/leads",            icon: MessageSquare, label: "Lead Management", perm: "leads" },
    ]},
    { section: "Equipment Cloud", items: [
      { to: "/admin/tenants", icon: Building2, label: "Fleet Tenants (SaaS)", adminOnly: true },
    ]},
    { section: "Service Network", items: [
      { to: "/amc",             icon: Handshake, label: "AMC / FMC" },
      { to: "/breakdown",       icon: Siren,     label: "Breakdown 24×7" },
      { to: "/intel",           icon: BarChart3, label: "Intel Hub" },
      { to: "/admin/intel",     icon: BarChart3, label: "Intel Reports", adminOnly: true },
    ]},
    { section: "Enterprise Solutions", items: [
      { to: "/admin/onboard",         icon: UserPlus,   label: "Onboard",       perm: "registrations" },
      { to: "/admin/approvals",       icon: CheckSquare,label: "Approvals",     adminOnly: true },
      { to: "/admin/approval-queue",  icon: CheckSquare,label: "Approval Queue",adminOnly: true },
      { to: "/admin/bulk-upload",     icon: UserPlus,   label: "Bulk Upload",   perm: "excel_upload" },
      { to: "/admin/all-deals",       icon: LayoutGrid, label: "All Deals · 17 Verticals", adminOnly: true },
      { to: "/admin/deals",           icon: LayoutGrid, label: "Deals (legacy)",perm: "deals" },
    ]},
    { section: "CRM Pipeline", items: [
      { to: "/admin/clients",             icon: Users,     label: "Client Master",     adminOnly: true },
      { to: "/admin/clients/analytics",   icon: BarChart3, label: "Client Analytics",  adminOnly: true },
      { to: "/admin/requirement-entry",   icon: UserPlus,  label: "Manual Req. Entry", adminOnly: true },
      { to: "/admin/crm-quotes",          icon: FileText,  label: "Quotes & CS",       adminOnly: true },
      { to: "/admin/quote-repository",    icon: FileText,  label: "Quote Repository",  adminOnly: true },
    ]},
    { section: "EquipmentHub Connect", items: [
      { to: "/connect/dashboard",         icon: LayoutGrid, label: "Connect Workspace" },
      { to: "/connect/branding",          icon: Crown,      label: "White Label Studio" },
      { to: "/admin/connect/plans",       icon: Crown,      label: "Plans & Governance", adminOnly: true },
    ]},
    { section: "Business Services", items: [
      { to: "/admin/invoices",           icon: FileText,  label: "Invoices",          perm: "invoices" },
      { to: "/admin/data-room",          icon: ShieldCheck, label: "Investor Data Room", adminOnly: true },
      { to: "/admin/offers",             icon: Crown,     label: "Offers",            adminOnly: true },
      { to: "/admin/ads",                icon: Crown,     label: "Ads & Featured",    adminOnly: true },
      { to: "/admin/credits",            icon: Crown,     label: "Lead Credits",      perm: "memberships" },
      { to: "/admin/revenue-tabs",       icon: LineChart, label: "Revenue Dashboard · 4 tabs", adminOnly: true },
      { to: "/admin/revenue",            icon: LineChart, label: "Revenue (legacy)",  adminOnly: true, perm: "revenue" },
      { to: "/admin/revenue-dashboard",  icon: LineChart, label: "Rev · 6 streams",   adminOnly: true },
      { to: "/admin/revenue-governance", icon: LineChart, label: "Revenue Governance", adminOnly: true },
      { to: "/admin/vertical-config",    icon: LayoutGrid,label: "Vertical Config Master", adminOnly: true },
    ]},
    { section: "AI & Intelligence", items: [
      { to: "/admin/ai-workforce",   icon: Bot, label: "AI Workforce", adminOnly: true },
      { to: "/admin/ai-marketing",   icon: Bot, label: "AI Marketing Manager", adminOnly: true },
      { to: "/admin/crm-audit",      icon: Bot, label: "AI CRM Audit",  adminOnly: true },
      { to: "/admin/crm-email-budget", icon: Bot, label: "CRM Email Budget", adminOnly: true },
    ]},
    { section: "Partner Network", items: [
      { to: "/admin/partners",         icon: Handshake, label: "Partner Network",      adminOnly: true },
      { to: "/admin/partner-config",   icon: LayoutGrid, label: "Partner Config",       adminOnly: true },
    ]},
    { section: "Communication", items: [
      { to: "/admin/whatsapp",             icon: MessageSquare, label: "WhatsApp (Meta)",        adminOnly: true },
      { to: "/admin/communication-center", icon: MessageSquare, label: "Communication Center", adminOnly: true },
      { to: "/admin/crm-templates-preview", icon: Mail, label: "CRM Template Gallery", adminOnly: true },
      { to: "/admin/inbound-emails",       icon: MessageSquare, label: "Inbound Emails",        adminOnly: true },
    ]},
    { section: "HR & People", items: [
      { to: "/admin/my-dashboard",     icon: LayoutDashboard, label: "My Dashboard" },
      { to: "/admin/team",             icon: UsersRound, label: "Team Members",   adminOnly: true, perm: "employee_management" },
      { to: "/admin/employees",        icon: ShieldCheck,label: "Employee Master",perm: "employee_management" },
      { to: "/admin/hrms",             icon: UsersRound, label: "HRMS",           adminOnly: true },
      { to: "/admin/permissions",      icon: ShieldCheck,label: "Permissions",    adminOnly: true },
      { to: "/admin/access-requests",  icon: KeyRound,   label: "Access Requests" },
    ]},
    { section: "Platform & Governance", items: [
      { to: "/admin/company-profile",     icon: Building2,  label: "Company Profile",    adminOnly: true },
      { to: "/admin/crm-automation",     icon: Bot,        label: "CRM Automation",     adminOnly: true },
      { to: "/admin/config",              icon: Crown,      label: "Configuration",      adminOnly: true },
      { to: "/admin/security",            icon: ShieldCheck,label: "Security Console",   adminOnly: true },
      { to: "/admin/foundation",          icon: LayoutGrid, label: "Foundation Modules", adminOnly: true },
      { to: "/admin/push-analytics",      icon: BellRing,   label: "Push Analytics",     adminOnly: true },
      { to: "/admin/platform-taxonomy",   icon: LayoutGrid, label: "Platform Taxonomy",  adminOnly: true },
      { to: "/admin/integrations",        icon: KeyRound,   label: "Integrations",       adminOnly: true },
    ]},
  ],
};

// Filter helper — applies isSupport rule to a flat items array
const filterItems = (items, isSupport) => items.filter((n) => !(isSupport && n.adminOnly));

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const [showChangePw, setShowChangePw] = useState(false);
  const [showDevices, setShowDevices] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  // Support role uses admin sidebar (but hide adminOnly items)
  const roleKey = user?.role === "support" ? "admin" : user?.role;
  const isSupport = user?.role === "support";
  const rawNav = navByRole[roleKey] || [];
  // Admin nav is grouped by section; other roles are flat arrays.
  const isGrouped = roleKey === "admin";
  const navSections = isGrouped
    ? rawNav
        .map((sec) => ({ ...sec, items: filterItems(sec.items, isSupport) }))
        .filter((sec) => sec.items.length > 0)
    : [{ section: null, items: filterItems(rawNav, isSupport) }];
  const flatNav = navSections.flatMap((s) => s.items);
  const loc = useLocation();

  const avatarSrc = user?.avatar_url
    ? (user.avatar_url.startsWith("http") ? user.avatar_url : `${api.defaults.baseURL}${user.avatar_url}`)
    : null;

  return (
    <div className="min-h-screen bg-[radial-gradient(1200px_600px_at_100%_-10%,rgba(249,115,22,0.06),transparent_50%),linear-gradient(180deg,#f8fafc_0%,#eef2f7_100%)] flex">
      <aside className="hidden md:flex w-56 xl:w-64 bg-gradient-to-b from-[#0B1F3A] via-[#0F2A4F] to-[#0B1F3A] text-white flex-col shrink-0 sticky top-0 h-screen shadow-[2px_0_20px_-10px_rgba(11,31,58,0.6)]" data-testid="dashboard-sidebar">
        {/* Saffron kinetic hairline on right edge — anchors the sidebar to the brand */}
        <span className="absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-ehi-accent/60 to-transparent" aria-hidden />

        <Link to="/" className="flex items-center gap-2.5 px-5 h-16 border-b border-white/10 hover:bg-white/5 transition-colors">
          <img src={BRAND.logoUrl} alt="EquipmentHub" className="h-11 w-11 object-contain" style={{ mixBlendMode: "darken" }} />
          <div className="leading-tight">
            <div className="font-display font-black text-sm text-white">EquipmentHub</div>
            <div className="text-[9px] tracking-[0.22em] uppercase text-ehi-accent font-bold" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>{user?.role} console</div>
          </div>
        </Link>
        <nav className="p-3 flex-1 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10">
          {navSections.map((sec, si) => (
            <div key={sec.section || si} className={si === 0 ? "" : "pt-2 mt-2 border-t border-white/8"}>
              {sec.section && (
                <div
                  className="px-2 pb-1 text-[9px] font-black tracking-[0.22em] uppercase text-ehi-accent/80"
                  style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
                  data-testid={`sidebar-section-${sec.section.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {sec.section}
                </div>
              )}
              <div className="space-y-0.5">
                {sec.items.map((n) => (
                  <NavLink
                    key={`${sec.section || "flat"}-${n.to}-${n.label}`}
                    to={n.to}
                    end={n.end}
                    className={({ isActive }) =>
                      `relative flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors group ${
                        isActive
                          ? "bg-white/12 text-white shadow-[inset_3px_0_0_0_#F97316]"
                          : "text-slate-300 hover:bg-white/8 hover:text-white"
                      }`
                    }
                    data-testid={`sidebar-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {({ isActive }) => (
                      <>
                        <n.icon className={`w-4 h-4 shrink-0 transition-colors ${isActive ? "text-ehi-accent" : "text-slate-400 group-hover:text-white"}`} strokeWidth={2} />
                        <span className="truncate">{n.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 bg-black/15">
          <button
            onClick={() => setShowProfile(true)}
            className="w-full flex items-center gap-3 px-2 py-2 rounded-sm hover:bg-white/8 transition-colors text-left"
            data-testid="sidebar-edit-profile"
            title="Edit profile"
          >
            <div className="w-9 h-9 rounded-full overflow-hidden bg-ehi-accent text-white grid place-items-center text-sm font-bold shrink-0 ring-2 ring-white/20">
              {avatarSrc ? (
                <img src={avatarSrc} alt={user?.name} className="w-full h-full object-cover" data-testid="sidebar-avatar-img" />
              ) : (
                <span>{user?.name?.[0]?.toUpperCase()}</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
            </div>
            <UserCog className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          </button>
          <button onClick={() => setShowChangePw(true)} className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider font-bold text-slate-300 hover:text-white hover:bg-white/8 rounded-sm transition-colors" data-testid="sidebar-change-password">
            <KeyRound className="w-3.5 h-3.5" /> Change password
          </button>
          <button onClick={() => setShowDevices(true)} className="mt-1 w-full flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider font-bold text-slate-300 hover:text-white hover:bg-white/8 rounded-sm transition-colors" data-testid="sidebar-devices">
            <Activity className="w-3.5 h-3.5" /> Devices &amp; sessions
          </button>
          <button onClick={logout} className="mt-1 w-full flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider font-bold text-slate-300 hover:text-white hover:bg-red-500/20 rounded-sm transition-colors" data-testid="sidebar-logout">
            <LogOut className="w-3.5 h-3.5" /> Log out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        {/* Desktop top strip — houses the notification bell */}
        <div className="hidden md:flex sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 h-12 items-center justify-end px-6 gap-3">
          <NotificationBell />
        </div>
        <header className="md:hidden glass-nav sticky top-0 z-30">
          <div className="h-14 flex items-center justify-between px-4">
            <Link to="/" className="flex items-center gap-2">
              <img src={BRAND.logoUrl} alt="" className="h-10 w-10 object-contain" style={{ mixBlendMode: "darken" }} />
              <span className="font-display font-black text-sm text-ehi-primary">EquipmentHub</span>
            </Link>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <button onClick={() => setShowChangePw(true)} className="text-xs text-slate-600" data-testid="mobile-change-password">Password</button>
              <button onClick={() => setShowDevices(true)} className="text-xs text-slate-600" data-testid="mobile-devices">Devices</button>
              <button onClick={logout} className="text-xs text-slate-600" data-testid="mobile-logout">Log out</button>
            </div>
          </div>
          <nav className="flex overflow-x-auto border-t border-slate-200 bg-[#0B1F3A] scrollbar-thin scrollbar-thumb-white/20">
            {navSections.map((sec, si) => (
              <div key={sec.section || si} className="flex items-center shrink-0">
                {sec.section && (
                  <div className="px-3 py-2 text-[9px] font-black tracking-[0.22em] uppercase text-ehi-accent/80 whitespace-nowrap border-l border-white/10">
                    {sec.section}
                  </div>
                )}
                {sec.items.map((n, i) => (
                  <NavLink key={`m-${sec.section || 'flat'}-${n.to}-${n.label}-${i}`} to={n.to} end={n.end}
                    className={({ isActive }) => `px-3 py-2 text-[11px] font-bold uppercase tracking-wider whitespace-nowrap border-b-2 ${isActive ? "border-ehi-accent text-white" : "border-transparent text-slate-300"}`}>
                    {n.label}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
        </header>
        <main className="min-h-[calc(100vh-0px)] bg-[radial-gradient(1200px_600px_at_100%_-10%,rgba(249,115,22,0.06),transparent_50%),radial-gradient(900px_500px_at_-10%_20%,rgba(11,31,58,0.05),transparent_55%),linear-gradient(180deg,#f8fafc_0%,#eef2f7_100%)]">
          <TrialRibbon />
          <div className="p-4 md:p-8"><Outlet /></div>
        </main>
      </div>
      <ChangePasswordModal open={showChangePw} onClose={() => setShowChangePw(false)} />
      <DevicesModal open={showDevices} onClose={() => setShowDevices(false)} />
      <ProfileModal open={showProfile} onClose={() => setShowProfile(false)} />
    </div>
  );
}
